import { DownloadOutlined, EyeOutlined, FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import { type StepAudience } from '@bmo/form-core';
import { Alert, Button, message, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowTemplate } from '@/api/workflows';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';

import { localizedEntryLabel } from './entryLabel';
import { type FieldValue } from './FieldRenderer';
import { fileIcon } from './fileIcon';
import { downloadFileToDevice, openFileInNewTab, previewBlobFor } from './filePreviewActions';
import {
  applicablePdfDocs,
  buildFilledPdfBlobUrl,
  type PdfGenerator,
  triggerBlobDownload,
} from './pdfPreviewActions';
import type { UploadedFilesStore } from './uploadedFiles';
import { flattenUploadedFiles } from './uploadedFiles';

const { Paragraph, Text } = Typography;

/**
 * Final-step UI for `PdfGeneration`. Lists every PDF this application generates
 * for the advisor — one row per `StepFileEntry` whose gating conditions pass
 * against the current answers (see `applicablePdfDocs`).
 *
 * On mount every entry is generated SERVER-SIDE against the current answers
 * (ADR-0031 — this used to run pdf-lib in the browser), and the resulting Blob
 * URLs are cached in a ref so per-row Preview / Download are instant. If the
 * advisor navigates back and edits answers, remounting the step re-runs
 * generation. Rows without an uploaded template skip generation and fall back to
 * the existing stub message on click.
 *
 * The cache carries more weight than it used to: a repeat visit with unchanged
 * answers now saves a network round trip and a Lambda invocation, not just some
 * local CPU. `RateLimitPolicies.PdfPreview` is sized on the assumption that it
 * keeps working — see the fan-out note there.
 */
/**
 * FormShell-owned cache of filled PDFs, keyed by `StepFileEntry.id`.
 *
 * `values` AND `language` together decide whether an entry is still fresh.
 * Language matters twice over: it picks which uploaded file is filled, and it
 * picks which set of signature boxes gets its anchor stamped into the result —
 * so keying on values alone would serve the other language's document, carrying
 * the other language's anchors, after a language switch.
 *
 * FormShell's own unmount revokes every URL; this file never revokes on remount.
 */
export type PdfCache = Map<
  string,
  { values: Record<string, FieldValue>; language: 'en' | 'fr'; url: string }
>;

export function PdfGenerationStep({
  template,
  language,
  values,
  cache,
  uploadedFiles,
  generatePdf,
  audience = 'internal',
  pdfTemplateId,
}: {
  template: WorkflowTemplate;
  language: 'en' | 'fr';
  values: Record<string, FieldValue>;
  cache: PdfCache;
  uploadedFiles: UploadedFilesStore;
  /** Which endpoint fills the documents - authenticated or anonymous. Resolved once by
   *  FormShell and passed down, so this step never picks a client itself. */
  generatePdf: PdfGenerator;
  /** Who filled these answers in. Travels beside `generatePdf` because the two
   *  describe the same audience from opposite ends — which endpoint fills the
   *  document, and which answers are the customer's to have in it. */
  audience?: StepAudience;
  /** Which workflow the fill is requested against — see `buildFilledPdfBlobUrl`'s
   *  `requestTemplateId`. Differs from `template.id` only on a branched form, whose
   *  documents belong to the branch target rather than to the base form it is wearing the
   *  name of. Resolved once by FormShell, like `generatePdf`. */
  pdfTemplateId?: string;
}) {
  const { t, i18n } = useTranslation();
  // `language` here is the client's PDF language (it picks the files). The uploaded-document rows
  // below are named by their upload slots, which are advisor UI text — so they follow the UI.
  const uiLanguage: 'en' | 'fr' = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const docs = useMemo(
    () => applicablePdfDocs(template, language, values),
    [template, language, values],
  );
  // Flatten every advisor-uploaded file (named entries + optional bucket)
  // so the list rendering below is one uniform ul. Named entries expose
  // the entry label; bucket files fall back to the filename.
  const uploadedRows = useMemo(() => {
    const entryMeta: Record<string, { label: string; filenameTemplate?: string | null }> = {};
    for (const step of template.steps) {
      if (step.type !== 'AdditionalDocuments') continue;
      for (const e of step.additionalDocumentEntries ?? []) {
        entryMeta[e.id] = {
          label: localizedEntryLabel(e, uiLanguage),
          filenameTemplate: e.filenameTemplate,
        };
      }
    }
    return flattenUploadedFiles(uploadedFiles, entryMeta, values, template);
  }, [template, uploadedFiles, values, uiLanguage]);

  // Pre-generated Blob URL per entryId. `null` = generation failed (fall
  // back to on-click retry); missing key = still generating (or never
  // eligible if previewKey is null). Seed from cache on mount so revisiting
  // the step with unchanged answers renders instantly, no spinner blink.
  const [ready, setReady] = useState<Record<string, string | null>>(() => {
    const initial: Record<string, string | null> = {};
    for (const [entryId, entry] of cache) {
      if (entry.values === values && entry.language === language) initial[entryId] = entry.url;
    }
    return initial;
  });

  // Bumped by "Try again": re-runs the generation below, which skips every document already in
  // the cache, so only the ones that failed are asked for again.
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    // Local cancel flag so a stale generation run (values/template changed
    // mid-flight) can't overwrite the newer cycle's state.
    let cancelled = false;

    void (async () => {
      const eligible = docs.filter((d) => d.previewKey);
      await Promise.all(
        eligible.map(async (d) => {
          const existing = cache.get(d.entryId);
          if (existing && existing.values === values && existing.language === language) {
            // Cache hit — surface immediately so the UI never shows a
            // spinner on a step revisit where nothing changed.
            setReady((prev) =>
              prev[d.entryId] === existing.url ? prev : { ...prev, [d.entryId]: existing.url },
            );
            return;
          }
          try {
            const blobUrl = await buildFilledPdfBlobUrl(
              template,
              d.previewKey!,
              values,
              d.entryId,
              d.previewLanguage,
              generatePdf,
              audience,
              pdfTemplateId,
            );
            if (cancelled) {
              URL.revokeObjectURL(blobUrl);
              return;
            }
            // Values changed since last fill — revoke the stale URL before
            // replacing so we never leak more than one per entry.
            if (existing) URL.revokeObjectURL(existing.url);
            cache.set(d.entryId, { values, language, url: blobUrl });
            setReady((prev) => ({ ...prev, [d.entryId]: blobUrl }));
          } catch {
            if (cancelled) return;
            setReady((prev) => ({ ...prev, [d.entryId]: null }));
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
      // NO revoke here — FormShell owns lifecycle. Revoking on child unmount
      // would blow away the cache we're trying to preserve.
    };
    // `generatePdf` is here for exhaustive-deps and cannot re-fire this effect in practice:
    // FormShell resolves it once to either `workflowsApi.generatePdfPreview` or the public
    // client's method, both of which are stable object members rather than fresh closures.
  }, [docs, template, values, cache, language, generatePdf, audience, pdfTemplateId, retryTick]);

  // SW-SCG-008: a document whose generation failed used to sit under "1 document is ready to
  // download" with nothing to say otherwise until a click raised a toast. It is now named as
  // failed, the intro counts only the documents that ARE ready, and the advisor can try again.
  // (Wording is ours until BMO's technical-issue copy, INFLOWS-449, is settled.)
  const failedIds = docs
    .filter((d) => d.previewKey && ready[d.entryId] === null)
    .map((d) => d.entryId);
  const retryFailed = () => {
    setReady((prev) => Object.fromEntries(Object.entries(prev).filter(([, url]) => url !== null)));
    setRetryTick((n) => n + 1);
  };

  if (docs.length === 0 && uploadedRows.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Paragraph style={{ marginBottom: 0, color: '#374151', fontSize: 14 }}>
          {t('formRenderer.pdfGeneration.empty')}
        </Paragraph>
      </div>
    );
  }

  // Preview: prefer cached URL; regenerate on demand if cache missing or
  // errored (transient S3 hiccup shouldn't dead-end the row).
  const handlePreview = async (
    label: string,
    entryId: string,
    key: string | null,
    keyLanguage: 'en' | 'fr',
  ) => {
    if (!key) {
      void message.info(t('formRenderer.pdfGeneration.stubPreview', { name: label }));
      return;
    }
    const cached = ready[entryId];
    if (cached) {
      window.open(cached, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const blobUrl = await buildFilledPdfBlobUrl(
        template,
        key,
        values,
        entryId,
        keyLanguage,
        generatePdf,
        audience,
        pdfTemplateId,
      );
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch (err) {
      handleApiError(err, t, t('formRenderer.pdfGeneration.previewFailed'));
    }
  };

  const handleDownload = async (
    label: string,
    fileName: string,
    entryId: string,
    key: string | null,
    keyLanguage: 'en' | 'fr',
  ) => {
    if (!key) {
      void message.info(t('formRenderer.pdfGeneration.stubDownload', { name: label }));
      return;
    }
    const cached = ready[entryId];
    if (cached) {
      // Reuse the pre-gen URL — download triggers reads it into the browser
      // download stream so we don't revoke this instance's cache; the effect
      // cleanup will handle it on unmount / regeneration.
      const a = document.createElement('a');
      a.href = cached;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    try {
      const blobUrl = await buildFilledPdfBlobUrl(
        template,
        key,
        values,
        entryId,
        keyLanguage,
        generatePdf,
        audience,
        pdfTemplateId,
      );
      triggerBlobDownload(blobUrl, fileName);
    } catch (err) {
      handleApiError(err, t, t('formRenderer.pdfGeneration.previewFailed'));
    }
  };

  // Per-uploaded-file Preview / Download share the same helpers as the
  // AdditionalDocuments step so behavior (popup-blocked toast, revoke
  // timing, filename preservation) stays identical across the flow.
  const previewUploaded = (file: File): void => openFileInNewTab(previewBlobFor(file), t);
  const downloadUploaded = (file: File): void => downloadFileToDevice(file);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {failedIds.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={t('formRenderer.pdfGeneration.generateFailed', { count: failedIds.length })}
          action={
            <Button size="small" onClick={retryFailed}>
              {t('formRenderer.pdfGeneration.retry')}
            </Button>
          }
        />
      )}
      {docs.length - failedIds.length > 0 && (
        <Paragraph
          strong
          style={{ marginBottom: 0, color: '#111827', fontSize: 14, fontWeight: 600 }}
        >
          {t('formRenderer.pdfGeneration.intro', { count: docs.length - failedIds.length })}
        </Paragraph>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {docs.map((d) => {
          // Only rows with an uploaded template participate in pre-gen; the
          // spinner state is purely visual — the buttons stay clickable
          // throughout and fall back to on-click generation if pressed early.
          const pending = d.previewKey !== null && !(d.entryId in ready);
          return (
            <li
              key={d.entryId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#fff',
              }}
            >
              <FilePdfOutlined style={{ color: '#dc2626', fontSize: 22 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="bmo-doc-row__name"
                  style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}
                >
                  {/* The name the advisor downloads (e.g. RDSP1_Smith_09252026.pdf), which is how
                      BMO's story and QA identify each document (QA DRV-016/019). The originator's
                      label — what the document IS — rides underneath. */}
                  {d.fileName}
                </div>
                <Text
                  className="bmo-doc-row__name"
                  type={ready[d.entryId] === null ? 'danger' : 'secondary'}
                  style={{ fontSize: 12 }}
                >
                  {pending
                    ? t('formRenderer.pdfGeneration.generating')
                    : ready[d.entryId] === null
                      ? t('formRenderer.pdfGeneration.rowFailed', { name: d.label })
                      : d.label}
                </Text>
              </div>
              <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
                <Tooltip title={t('formRenderer.pdfGeneration.previewOne')}>
                  <Button
                    {...ICON_BTN_PROPS}
                    aria-label={t('formRenderer.pdfGeneration.previewOne')}
                    icon={pending ? <LoadingOutlined /> : <EyeOutlined />}
                    onClick={() =>
                      void handlePreview(d.label, d.entryId, d.previewKey, d.previewLanguage)
                    }
                  />
                </Tooltip>
                <Tooltip title={t('formRenderer.pdfGeneration.downloadOne')}>
                  <Button
                    {...ICON_BTN_PROPS}
                    aria-label={t('formRenderer.pdfGeneration.downloadOne')}
                    type="primary"
                    icon={pending ? <LoadingOutlined /> : <DownloadOutlined />}
                    onClick={() =>
                      void handleDownload(
                        d.label,
                        d.fileName,
                        d.entryId,
                        d.previewKey,
                        d.previewLanguage,
                      )
                    }
                  />
                </Tooltip>
              </div>
            </li>
          );
        })}
        {uploadedRows.map((r) => (
          <li
            key={`uploaded:${r.uid}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              background: '#fff',
            }}
          >
            {fileIcon(r.file.name, 22)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="bmo-doc-row__name"
                style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}
              >
                {r.label}
              </div>
              <Text className="bmo-doc-row__name" type="secondary" style={{ fontSize: 12 }}>
                {t('formRenderer.pdfGeneration.uploadedSubtitle')}
              </Text>
            </div>
            <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
              <Tooltip title={t('formRenderer.pdfGeneration.previewOne')}>
                <Button
                  {...ICON_BTN_PROPS}
                  aria-label={t('formRenderer.pdfGeneration.previewOne')}
                  icon={<EyeOutlined />}
                  onClick={() => previewUploaded(r.file)}
                />
              </Tooltip>
              <Tooltip title={t('formRenderer.pdfGeneration.downloadOne')}>
                <Button
                  {...ICON_BTN_PROPS}
                  aria-label={t('formRenderer.pdfGeneration.downloadOne')}
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadUploaded(r.file)}
                />
              </Tooltip>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
